// Vollea — Fixture generator modal (organiser/club admin/super admin)
import React, { useState } from "react";
import { IoClose } from "react-icons/io5";
import { Btn } from "./ui-vollea";
import { api } from "../lib/api";
import { C, SP, R } from "../lib/theme";

export default function FixtureGeneratorModal({ league, onClose, onGenerated }) {
  const [division, setDivision] = useState(league.divisions?.[0] || "");
  const [double, setDouble] = useState(false);
  const [weekly, setWeekly] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true); setMsg("");
    try {
      const body = { division, double, weekly };
      if (startDate) body.start_date = new Date(startDate).toISOString();
      const r = await api.post(`/leagues/${league.league_id}/fixtures/generate`, body);
      setMsg(`Generated ${r.created} match${r.created === 1 ? "" : "es"} (${r.format}).`);
      setTimeout(() => { onGenerated?.(); onClose(); }, 1600);
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  };

  const label = { color: C.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 6, marginTop: SP.md, textTransform: "uppercase", display: "block" };
  const input = { width: "100%", background: C.surface2, borderRadius: R.md, border: `1px solid ${C.border}`, color: C.text, padding: `0 ${SP.lg}px`, height: 52, fontSize: 15, outline: "none" };

  return (
    <div
      role="dialog"
      data-testid="fixture-gen-modal"
      style={{
        position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.7)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 480, background: C.surface, borderTop: `1px solid ${C.border}`,
          borderRadius: `${R.lg}px ${R.lg}px 0 0`, padding: `${SP.lg}px ${SP.lg}px 40px`,
          maxHeight: "90vh", overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: SP.md }}>
          <h2 style={{ color: C.text, fontWeight: 700, fontSize: 22, margin: 0, letterSpacing: 0.4 }}>Generate fixtures</h2>
          <button type="button" onClick={onClose} data-testid="fixture-gen-close" style={{ color: C.textMuted, padding: 4 }}>
            <IoClose size={26} />
          </button>
        </div>
        <p style={{ color: C.textMuted, fontSize: 13, margin: "0 0 4px" }}>
          Round-robin pairings between every team in the selected division. Re-run to add new teams without duplicating.
        </p>

        <label style={label}>Division</label>
        <select value={division} onChange={(e) => setDivision(e.target.value)} data-testid="fixture-gen-division" style={input} required>
          {(league.divisions || []).map((d) => {
            const optLabel = `Div ${d}`;
            return <option key={d} value={d} style={{ background: C.surface }}>{optLabel}</option>;
          })}
        </select>

        <label style={label}>Start date (optional)</label>
        <input
          type="datetime-local"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          data-testid="fixture-gen-startdate"
          style={input}
        />

        <div style={{ marginTop: SP.lg, display: "flex", flexDirection: "column", gap: SP.md }}>
          <ToggleRow
            label="Double round-robin"
            sub="Each pairing plays twice (home & away)."
            value={double}
            onChange={() => setDouble((v) => !v)}
            testid="fixture-gen-double"
          />
          <ToggleRow
            label="Weekly schedule"
            sub="Spread one fixture per week from the start date."
            value={weekly}
            onChange={() => setWeekly((v) => !v)}
            testid="fixture-gen-weekly"
            disabled={!startDate}
          />
        </div>

        {msg ? (
          <p data-testid="fixture-gen-msg" style={{ color: msg.startsWith("Generated") ? C.lime : C.error, fontSize: 13, marginTop: SP.md }}>{msg}</p>
        ) : null}

        <div style={{ height: SP.lg }} />
        <Btn label="Generate fixtures" loading={busy} testid="fixture-gen-submit" type="submit" onClick={submit} />
      </form>
    </div>
  );
}

function ToggleRow({ label, sub, value, onChange, testid, disabled }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SP.md, opacity: disabled ? 0.5 : 1 }}>
      <div>
        <div style={{ color: C.text, fontWeight: 700, fontSize: 14 }}>{label}</div>
        <div style={{ color: C.textMuted, fontSize: 12 }}>{sub}</div>
      </div>
      <button
        type="button"
        data-testid={testid}
        onClick={disabled ? undefined : onChange}
        style={{
          position: "relative", width: 46, height: 26, borderRadius: 13,
          background: value ? C.lime : C.surface3, transition: "background 0.2s", flexShrink: 0,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        <span style={{
          position: "absolute", top: 3, left: value ? 23 : 3, width: 20, height: 20, borderRadius: 10,
          background: value ? C.onLime : C.text, transition: "left 0.2s",
        }} />
      </button>
    </div>
  );
}
